import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { InlineTextEditor } from '@/components/ui/inline-text-editor';
import { Apple, Smartphone, Gift, ChevronDown, Repeat } from 'lucide-react';

interface LoyaltyFormUIText {
  headerTitle: string;
  headerSubtitle: string;
  pageTitle: string;
  pageSubtitle: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  consentText: string;
  appleButtonText: string;
  googleButtonText: string;
  // Existing customer lookup text
  lookupSectionHeader?: string;
  lookupPhoneLabel?: string;
  lookupPhonePlaceholder?: string;
  lookupOrText?: string;
  lookupBarcodeLabel?: string;
  lookupButtonText?: string;
  lookupNotFoundMessage?: string;
  lookupFoundMessage?: string;
}

interface LoyaltyFormPreviewProps {
  collectEmail: boolean;
  emailRequired: boolean;
  collectPhone: boolean;
  phoneRequired: boolean;
  requireMarketingConsent: boolean;
  enableLookup?: boolean;
  uiText: LoyaltyFormUIText;
  businessName?: string;
  programName?: string;
  logoUrl?: string; // Deprecated - for backward compatibility
  appleLogoUrl?: string;
  googleLogoUrl?: string;
  previewPlatform?: 'apple' | 'google';
  onPreviewPlatformChange?: (platform: 'apple' | 'google') => void;
  editingEnabled?: boolean;
  onUpdateUIText?: (field: keyof LoyaltyFormUIText, value: string) => void;
}

// Optimized mobile size - iPhone 15 Pro Max proportions
const MOBILE_SIZE = { width: 320, height: 590, label: 'iPhone 15 Pro Max' };

export const LoyaltyFormPreview: React.FC<LoyaltyFormPreviewProps> = ({
  collectEmail,
  emailRequired,
  collectPhone,
  phoneRequired,
  requireMarketingConsent,
  enableLookup = false,
  uiText,
  businessName = 'Your Business',
  programName = 'Loyalty Program',
  logoUrl,
  appleLogoUrl,
  googleLogoUrl,
  previewPlatform = 'apple',
  onPreviewPlatformChange,
  editingEnabled = false,
  onUpdateUIText
}) => {
  const currentDevice = MOBILE_SIZE;
  const [isEditingConsent, setIsEditingConsent] = React.useState(false);
  const [lookupExpanded, setLookupExpanded] = React.useState(true); // Expanded by default in preview

  // Get the logo URL based on selected platform
  const getDisplayLogoUrl = () => {
    if (previewPlatform === 'google' && googleLogoUrl) {
      return googleLogoUrl;
    }
    if (previewPlatform === 'apple' && appleLogoUrl) {
      return appleLogoUrl;
    }
    // Fallback to any available logo
    return appleLogoUrl || googleLogoUrl || logoUrl || null;
  };

  const displayLogoUrl = getDisplayLogoUrl();

  // State for lookup button editing (must be at top level to avoid hooks violation)
  const [isEditingFindBtn, setIsEditingFindBtn] = React.useState(false);
  const [editFindValue, setEditFindValue] = React.useState(uiText.lookupButtonText || "Find My Account");
  const [isEditingScanBtn, setIsEditingScanBtn] = React.useState(false);
  const [editScanValue, setEditScanValue] = React.useState(uiText.lookupBarcodeLabel || "Scan your barcode");

  // Sync edit values with uiText changes
  React.useEffect(() => {
    if (!isEditingFindBtn) {
      setEditFindValue(uiText.lookupButtonText || "Find My Account");
    }
  }, [uiText.lookupButtonText, isEditingFindBtn]);

  React.useEffect(() => {
    if (!isEditingScanBtn) {
      setEditScanValue(uiText.lookupBarcodeLabel || "Scan your barcode");
    }
  }, [uiText.lookupBarcodeLabel, isEditingScanBtn]);

  // Helper to render privacy consent with last word as link (matches review form style)
  const renderConsentText = () => {
    const words = uiText.consentText.trim().split(/\s+/);
    const lastWord = words[words.length - 1];
    const beforeLastWord = words.slice(0, -1).join(' ');

    return (
      <>
        {beforeLastWord}{' '}
        <span className="text-primary underline">
          {lastWord}
        </span>
        {' *'}
      </>
    );
  };

  // EditableUIPlaceholderInput component for editing placeholders
  const EditableUIPlaceholderInput: React.FC<{
    id: string;
    type?: string;
    placeholder: string;
    placeholderKey: keyof LoyaltyFormUIText;
    className?: string;
  }> = ({ id, type = "text", placeholder, placeholderKey, className = "" }) => {
    const [isEditingPlaceholder, setIsEditingPlaceholder] = React.useState(false);
    const [editPlaceholderValue, setEditPlaceholderValue] = React.useState(placeholder);

    React.useEffect(() => {
      if (!isEditingPlaceholder) {
        setEditPlaceholderValue(placeholder);
      }
    }, [placeholder, isEditingPlaceholder]);

    const handlePlaceholderSave = () => {
      if (editPlaceholderValue.trim() !== placeholder && onUpdateUIText) {
        onUpdateUIText(placeholderKey, editPlaceholderValue.trim());
      }
      setIsEditingPlaceholder(false);
    };

    const handlePlaceholderCancel = () => {
      setEditPlaceholderValue(placeholder);
      setIsEditingPlaceholder(false);
    };

    return (
      <div className="relative group">
        <Input
          id={id}
          type={type}
          placeholder={placeholder}
          className={className}
          disabled
        />
        {editingEnabled && onUpdateUIText && (
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsEditingPlaceholder(true);
              setEditPlaceholderValue(placeholder);
            }}
            className="absolute top-2 right-2 w-4 h-4 bg-black/20 hover:bg-black/30 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-20"
            title="Edit placeholder text"
          >
            <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}

        {/* Placeholder editing overlay */}
        {isEditingPlaceholder && editingEnabled && onUpdateUIText && (
          <div
            className="absolute top-0 left-0 right-0 bg-white border-2 border-blue-500 rounded-md p-2 z-30 shadow-lg"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={editPlaceholderValue}
              onChange={(e) => setEditPlaceholderValue(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handlePlaceholderSave();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  handlePlaceholderCancel();
                }
              }}
              onBlur={handlePlaceholderSave}
              className="w-full text-xs bg-transparent border-none outline-none"
              placeholder="Enter placeholder text"
              autoFocus
            />
            <div className="text-xs text-muted-foreground mt-1">
              Press Enter to save, Escape to cancel
            </div>
          </div>
        )}
      </div>
    );
  };

  // EditableButton component for editing button text
  const EditableButton: React.FC<{
    onClick: () => void;
    className: string;
    textKey: keyof LoyaltyFormUIText;
    children?: React.ReactNode;
  }> = ({ onClick, className, textKey, children }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [editValue, setEditValue] = React.useState(uiText[textKey]);

    React.useEffect(() => {
      if (!isEditing) {
        setEditValue(uiText[textKey]);
      }
    }, [uiText[textKey], isEditing]);

    const handleSave = () => {
      if (editValue.trim() !== uiText[textKey] && onUpdateUIText) {
        onUpdateUIText(textKey, editValue.trim());
      }
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditValue(uiText[textKey]);
      setIsEditing(false);
    };

    if (isEditing && editingEnabled && onUpdateUIText) {
      return (
        <div className="relative w-full">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
            onBlur={handleSave}
            className="w-full bg-white/90 text-gray-900 py-2 px-3 rounded-lg text-xs font-medium border-2 border-blue-500 focus:outline-none"
            autoFocus
          />
        </div>
      );
    }

    return (
      <div className="relative group">
        <button onClick={onClick} className={className} disabled>
          {children}
          {uiText[textKey]}
        </button>
        {editingEnabled && onUpdateUIText && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
              setEditValue(uiText[textKey]);
            }}
            className="absolute top-1 right-1 w-5 h-5 bg-black/20 hover:bg-black/30 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
            title="Edit button text"
          >
            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {/* Help hint - only show when editing is enabled */}
      {editingEnabled && (
        <div className="flex items-center justify-center gap-1.5">
          <svg className="w-3.5 h-3.5 flex-shrink-0 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-muted-foreground">
            Click any text below to edit it directly
          </p>
        </div>
      )}

      {/* Device Frame */}
      <div className="relative mx-auto bg-gradient-to-b from-gray-400 to-gray-500 rounded-[2.5rem] p-3 shadow-2xl"
           style={{ width: currentDevice.width + 24, height: currentDevice.height + 24 }}>

        {/* Screen */}
        <div className="relative w-full h-full bg-gray-50 dark:bg-background rounded-[1.75rem] overflow-hidden">
          {/* Status Bar */}
          <div className="bg-white dark:bg-gray-800 px-4 py-2 flex justify-between items-center text-xs text-gray-900 dark:text-gray-100">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              {/* Signal bars - Full signal (4 bars) */}
              <div className="flex gap-[2px] items-end">
                <div className="w-1 h-1.5 bg-current rounded-full"></div>
                <div className="w-1 h-2 bg-current rounded-full"></div>
                <div className="w-1 h-2.5 bg-current rounded-full"></div>
                <div className="w-1 h-3 bg-current rounded-full"></div>
              </div>
              {/* WiFi icon - 2 out of 3 bars */}
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 20h.01M8.5 16.5a5 5 0 017 0M5 13a9 9 0 0114 0" opacity="0.4" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.5 16.5a5 5 0 017 0M12 20h.01" />
              </svg>
              {/* Battery - Gray/not charging */}
              <div className="w-5 h-2.5 border-2 border-current rounded-[3px] relative">
                <div className="absolute -right-[3px] top-1/2 -translate-y-1/2 w-[2px] h-1.5 bg-current rounded-r-sm"></div>
                <div className="w-3/4 h-full bg-gray-400 dark:bg-gray-500 rounded-[1px]"></div>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="h-full overflow-y-auto pb-20 bg-gray-50 dark:bg-background">
            <div className="p-4 space-y-4">
              {/* Header */}
              <div className="text-center space-y-2">
                <div
                  className={`flex items-center justify-center mx-auto relative group ${editingEnabled && appleLogoUrl && googleLogoUrl && onPreviewPlatformChange ? 'cursor-pointer' : ''}`}
                  style={{ height: '64px' }}
                  onClick={() => {
                    if (editingEnabled && appleLogoUrl && googleLogoUrl && onPreviewPlatformChange) {
                      onPreviewPlatformChange(previewPlatform === 'apple' ? 'google' : 'apple');
                    }
                  }}
                >
                  {displayLogoUrl ? (
                    <img
                      src={displayLogoUrl}
                      alt="Logo"
                      className={previewPlatform === 'google' ? 'w-16 h-16 rounded-full object-cover' : 'h-16 max-w-full object-contain'}
                    />
                  ) : (
                    <Gift className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  )}

                  {/* Platform switcher button - visual indicator that logo is clickable */}
                  {editingEnabled && appleLogoUrl && googleLogoUrl && onPreviewPlatformChange && (
                    <div
                      className="absolute top-0 right-0 w-6 h-6 bg-black/20 hover:bg-black/30 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 pointer-events-none"
                      title={`Switch to ${previewPlatform === 'apple' ? 'Google' : 'Apple'} logo`}
                    >
                      <span className="text-white text-sm font-bold">⇄</span>
                    </div>
                  )}
                </div>
                <h1 className="text-base font-bold text-gray-900 dark:text-gray-100">
                  {editingEnabled && onUpdateUIText ? (
                    <InlineTextEditor
                      value={uiText.headerTitle}
                      onSave={(value) => onUpdateUIText('headerTitle', value)}
                      placeholder={programName}
                      className="text-base font-bold text-gray-900 dark:text-gray-100"
                    />
                  ) : (
                    uiText.headerTitle
                  )}
                </h1>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {editingEnabled && onUpdateUIText ? (
                    <InlineTextEditor
                      value={uiText.headerSubtitle}
                      onSave={(value) => onUpdateUIText('headerSubtitle', value)}
                      placeholder="Collect points, gain rewards"
                      className="text-xs text-gray-600 dark:text-gray-400"
                    />
                  ) : (
                    uiText.headerSubtitle
                  )}
                </p>
              </div>

              {/* Existing Customer Lookup Section */}
              {enableLookup && (
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-2">
                  {/* Collapsible Header */}
                  <button
                    onClick={() => setLookupExpanded(!lookupExpanded)}
                    className="w-full flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                  >
                    <p className="text-xs font-medium text-blue-900 dark:text-blue-100">
                      {editingEnabled && onUpdateUIText ? (
                        <InlineTextEditor
                          value={uiText.lookupSectionHeader || "💡 Already have a loyalty card?"}
                          onSave={(value) => onUpdateUIText('lookupSectionHeader', value)}
                          placeholder="💡 Already have a loyalty card?"
                          className="text-xs font-medium text-blue-900 dark:text-blue-100"
                        />
                      ) : (
                        uiText.lookupSectionHeader || "💡 Already have a loyalty card?"
                      )}
                    </p>
                    <ChevronDown
                      className={`h-4 w-4 text-blue-900 dark:text-blue-100 transition-transform duration-200 flex-shrink-0 ${
                        lookupExpanded ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Collapsible Content */}
                  {lookupExpanded && (
                    <div className="space-y-1.5 pt-1">
                    <Label htmlFor="lookup-phone" className="text-[10px] text-blue-800 dark:text-blue-200">
                      {editingEnabled && onUpdateUIText ? (
                        <InlineTextEditor
                          value={uiText.lookupPhoneLabel || "Phone number"}
                          onSave={(value) => onUpdateUIText('lookupPhoneLabel', value)}
                          placeholder="Phone number"
                          className="text-[10px] text-blue-800 dark:text-blue-200"
                        />
                      ) : (
                        uiText.lookupPhoneLabel || "Phone number"
                      )}
                    </Label>
                    <div className="flex gap-1">
                      <EditableUIPlaceholderInput
                        id="lookup-phone"
                        type="tel"
                        placeholder={uiText.lookupPhonePlaceholder || "+1 (555) 000-0000"}
                        placeholderKey="lookupPhonePlaceholder"
                        className="h-7 text-xs flex-1 bg-white dark:bg-gray-900"
                      />
                      {/* Find My Account Button - Editable */}
                      {(() => {
                        if (isEditingFindBtn && editingEnabled && onUpdateUIText) {
                          return (
                            <input
                              type="text"
                              value={editFindValue}
                              onChange={(e) => setEditFindValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (editFindValue.trim() !== (uiText.lookupButtonText || "Find My Account")) {
                                    onUpdateUIText('lookupButtonText', editFindValue.trim());
                                  }
                                  setIsEditingFindBtn(false);
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setEditFindValue(uiText.lookupButtonText || "Find My Account");
                                  setIsEditingFindBtn(false);
                                }
                              }}
                              onBlur={() => {
                                if (editFindValue.trim() !== (uiText.lookupButtonText || "Find My Account")) {
                                  onUpdateUIText('lookupButtonText', editFindValue.trim());
                                }
                                setIsEditingFindBtn(false);
                              }}
                              className="h-7 px-2 text-[10px] font-medium border-2 border-blue-500 rounded-md focus:outline-none"
                              autoFocus
                            />
                          );
                        }

                        return (
                          <div className="relative group">
                            <button
                              className="h-7 px-2 bg-blue-600 dark:bg-blue-700 text-white text-[10px] rounded-md font-medium whitespace-nowrap"
                              disabled
                            >
                              {uiText.lookupButtonText || "Find My Account"}
                            </button>
                            {editingEnabled && onUpdateUIText && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsEditingFindBtn(true);
                                }}
                                className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/30 hover:bg-black/40 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                                title="Edit button text"
                              >
                                <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Or text - Editable */}
                    <div className="text-center">
                      <p className="text-[10px] text-blue-700 dark:text-blue-300">
                        {editingEnabled && onUpdateUIText ? (
                          <InlineTextEditor
                            value={uiText.lookupOrText || "or"}
                            onSave={(value) => onUpdateUIText('lookupOrText', value)}
                            placeholder="or"
                            className="text-[10px] text-blue-700 dark:text-blue-300"
                          />
                        ) : (
                          uiText.lookupOrText || "or"
                        )}
                      </p>
                    </div>

                    {/* Scan Barcode Button - Editable */}
                    {(() => {
                      if (isEditingScanBtn && editingEnabled && onUpdateUIText) {
                        return (
                          <input
                            type="text"
                            value={editScanValue}
                            onChange={(e) => setEditScanValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (editScanValue.trim() !== (uiText.lookupBarcodeLabel || "Scan your barcode")) {
                                  onUpdateUIText('lookupBarcodeLabel', editScanValue.trim());
                                }
                                setIsEditingScanBtn(false);
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setEditScanValue(uiText.lookupBarcodeLabel || "Scan your barcode");
                                setIsEditingScanBtn(false);
                              }
                            }}
                            onBlur={() => {
                              if (editScanValue.trim() !== (uiText.lookupBarcodeLabel || "Scan your barcode")) {
                                onUpdateUIText('lookupBarcodeLabel', editScanValue.trim());
                              }
                              setIsEditingScanBtn(false);
                            }}
                            className="w-full h-7 px-2 text-[10px] font-medium border-2 border-blue-500 rounded-md focus:outline-none text-center"
                            autoFocus
                          />
                        );
                      }

                      return (
                        <div className="relative group">
                          <button
                            className="w-full h-7 bg-white dark:bg-gray-900 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-[10px] rounded-md font-medium flex items-center justify-center gap-1"
                            disabled
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                            </svg>
                            {uiText.lookupBarcodeLabel || "Scan your barcode"}
                          </button>
                          {editingEnabled && onUpdateUIText && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsEditingScanBtn(true);
                              }}
                              className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/30 hover:bg-black/40 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                              title="Edit button text"
                            >
                              <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  )}
                </div>
              )}

              {/* Form Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 space-y-3">
                <div className="space-y-1">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {editingEnabled && onUpdateUIText ? (
                      <InlineTextEditor
                        value={uiText.pageTitle}
                        onSave={(value) => onUpdateUIText('pageTitle', value)}
                        placeholder="Get Your Loyalty Card"
                        className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                      />
                    ) : (
                      uiText.pageTitle
                    )}
                  </h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {editingEnabled && onUpdateUIText ? (
                      <InlineTextEditor
                        value={uiText.pageSubtitle}
                        onSave={(value) => onUpdateUIText('pageSubtitle', value)}
                        placeholder="Fill in your details..."
                        className="text-xs text-gray-600 dark:text-gray-400"
                      />
                    ) : (
                      uiText.pageSubtitle
                    )}
                  </p>
                </div>

                {/* Name Field - Always shown */}
                <div className="space-y-1">
                  <Label htmlFor="preview-name" className="text-xs">
                    {editingEnabled && onUpdateUIText ? (
                      <InlineTextEditor
                        value={uiText.nameLabel}
                        onSave={(value) => onUpdateUIText('nameLabel', value)}
                        placeholder="Full Name"
                        className="text-xs"
                      />
                    ) : (
                      uiText.nameLabel
                    )}{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <EditableUIPlaceholderInput
                    id="preview-name"
                    placeholder={uiText.namePlaceholder}
                    placeholderKey="namePlaceholder"
                    className="h-8 text-xs"
                  />
                </div>

                {/* Email Field - Conditional */}
                {collectEmail && (
                  <div className="space-y-1">
                    <Label htmlFor="preview-email" className="text-xs">
                      {editingEnabled && onUpdateUIText ? (
                        <InlineTextEditor
                          value={uiText.emailLabel}
                          onSave={(value) => onUpdateUIText('emailLabel', value)}
                          placeholder="Email Address"
                          className="text-xs"
                        />
                      ) : (
                        uiText.emailLabel
                      )}{' '}
                      {emailRequired && <span className="text-red-500">*</span>}
                    </Label>
                    <EditableUIPlaceholderInput
                      id="preview-email"
                      type="email"
                      placeholder={uiText.emailPlaceholder}
                      placeholderKey="emailPlaceholder"
                      className="h-8 text-xs"
                    />
                  </div>
                )}

                {/* Phone Field - Conditional */}
                {collectPhone && (
                  <div className="space-y-1">
                    <Label htmlFor="preview-phone" className="text-xs">
                      {editingEnabled && onUpdateUIText ? (
                        <InlineTextEditor
                          value={uiText.phoneLabel}
                          onSave={(value) => onUpdateUIText('phoneLabel', value)}
                          placeholder="Phone Number"
                          className="text-xs"
                        />
                      ) : (
                        uiText.phoneLabel
                      )}{' '}
                      {phoneRequired && <span className="text-red-500">*</span>}
                    </Label>
                    <EditableUIPlaceholderInput
                      id="preview-phone"
                      type="tel"
                      placeholder={uiText.phonePlaceholder}
                      placeholderKey="phonePlaceholder"
                      className="h-8 text-xs"
                    />
                  </div>
                )}

                {/* Marketing Consent - Conditional */}
                {requireMarketingConsent && (
                  <div className="flex items-start space-x-2 pt-2">
                    <Checkbox id="preview-consent" className="mt-0.5" disabled />
                    <Label htmlFor="preview-consent" className="text-[10px] text-gray-600 leading-tight cursor-pointer relative group">
                      {isEditingConsent && editingEnabled && onUpdateUIText ? (
                        <input
                          type="text"
                          value={uiText.consentText}
                          onChange={(e) => onUpdateUIText('consentText', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              setIsEditingConsent(false);
                            } else if (e.key === 'Escape') {
                              setIsEditingConsent(false);
                            }
                          }}
                          onBlur={() => setIsEditingConsent(false)}
                          autoFocus
                          className="w-full bg-white/20 border border-white/30 rounded px-2 py-1 text-[10px] text-gray-600 focus:bg-white/30 focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                        />
                      ) : (
                        <>
                          {renderConsentText()}
                          {editingEnabled && onUpdateUIText && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsEditingConsent(true);
                              }}
                              className="ml-2 inline-flex w-4 h-4 bg-black/20 hover:bg-black/30 rounded-full items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                              title="Edit consent text"
                            >
                              <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </Label>
                  </div>
                )}

                {/* Wallet Buttons - Both shown in preview for editing */}
                <div className="pt-2 space-y-2">
                  {/* Apple Wallet Button */}
                  <EditableButton
                    onClick={() => {}}
                    className="w-full h-8 bg-black text-white text-xs rounded-md font-medium flex items-center justify-center"
                    textKey="appleButtonText"
                  >
                    <Apple className="h-3 w-3 mr-1" />
                  </EditableButton>

                  {/* Google Wallet Button */}
                  <EditableButton
                    onClick={() => {}}
                    className="w-full h-8 bg-blue-600 text-white text-xs rounded-md font-medium flex items-center justify-center"
                    textKey="googleButtonText"
                  >
                    <Smartphone className="h-3 w-3 mr-1" />
                  </EditableButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
